import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "@/services/authService";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { NEW_APPLICATION, useApplication } from "@/contexts/ApplicationContext";
import { Button } from "@/components/ui/button";
import { Job } from "@/hooks/useJobQueries";

/**
 * Component that signs out the user and redirects to the basic details page
 */
const SignOutRedirect: React.FC<{ job: Job }> = ({ job }) => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { setApplication } = useApplication();

  const performSignOut = async () => {
    setIsLoading(true);
    try {
      // Clear application data from local storage
      localStorage.removeItem("currentApplicationId");
      localStorage.removeItem("currentJobId");

      // Sign out from Supabase auth
      const { success, error } = await signOut();
      setApplication(NEW_APPLICATION);
      if (success) {
        window.location.href = job.url;
      } else {
        console.error("Error starting application:", error);
        toast({
          title: "Error",
          description:
            "There was an issue starting your application. Please try again.",
          variant: "destructive",
        });
        navigate(`/`);
      }
    } catch (error) {
      console.error("Unexpected error during application start:", error);
    } finally {
      // Regardless of success or failure, we'll redirect to basic details
      setIsLoading(false);
    }
  };

  // Redirect to basic details page
  return (
    <Button
      onClick={performSignOut}
      className="w-full bg-honeycomb hover:bg-amber-500 text-white"
      disabled={isLoading}
    >
      {isLoading ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Starting application...
        </>
      ) : (
        "Apply Now"
      )}
    </Button>
  );
};

export default SignOutRedirect;
